import { useEffect, useRef, useState } from "react";
import { CalendarPlus, ChevronLeft, MapPinPlus, Plus, X } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { fetchTags } from "@/lib/tags";
import { useI18n } from "@/i18n";

const EMPTY = {
  title: "",
  description: "",
  event_date: "",
  category: "",
  location_url: "",
  phone: "",
  website_url: "",
  email: "",
  featured_image: "",
  submitter_email: "",
  website: "", // honeypot
};

function SubmitForm({ kind, onClose, onBack }) {
  const { t } = useI18n();
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [placeCategories, setPlaceCategories] = useState([]);
  const fileRef = useRef(null);
  const isEvent = kind === "event";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    fetchTags().then((tags) => setPlaceCategories(tags.filter((t) => t !== "events")));
  }, []);

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/public/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("featured_image", r.data.url);
    } catch (err) {
      setError(t("errImage"));
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!form.title.trim()) return setError(t("errTitle"));
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.submitter_email.trim()))
      return setError(t("errEmail"));
    if (isEvent && !form.event_date) return setError(t("errDate"));
    if (!isEvent && !form.category) return setError(t("errCategory"));
    setSubmitting(true);
    try {
      await api.post("/submit", { kind, ...form });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t("errGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="submit-modal" onClick={(e) => e.stopPropagation()} data-testid="submit-success">
        <button className="submit-close" onClick={onClose} aria-label="Close" data-testid="submit-close"><X size={18} /></button>
        <h2 className="submit-title">{t("thankYou")}</h2>
        <p className="submit-sub">
          {isEvent ? t("thankYouEvent") : t("thankYouPlace")}
        </p>
        <div className="submit-actions">
          <button className="btn-primary" onClick={onClose} data-testid="submit-done-btn">{t("done")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-modal" onClick={(e) => e.stopPropagation()} data-testid={`submit-form-${kind}`}>
      <button className="submit-close" onClick={onClose} aria-label="Close" data-testid="submit-close"><X size={18} /></button>
      {onBack ? (
        <button className="submit-back" onClick={onBack} data-testid="submit-back-btn">
          <ChevronLeft size={14} strokeWidth={2.5} /> {t("back")}
        </button>
      ) : null}
      <h2 className="submit-title">{isEvent ? t("submitEventTitle") : t("submitPlaceTitle")}</h2>
      <p className="submit-sub">{t("submitSub")}</p>

      <label className="submit-label">{isEvent ? t("fEventName") : t("fPlaceName")} *</label>
      <input className="submit-input" value={form.title} onChange={(e) => set("title", e.target.value)}
        placeholder={isEvent ? "Full moon beach party" : "Tico Surf Shack"} data-testid="submit-title-input" />

      {isEvent ? (
        <>
          <label className="submit-label">{t("fEventDate")} *</label>
          <input type="date" className="submit-input" value={form.event_date}
            onChange={(e) => set("event_date", e.target.value)} data-testid="submit-date-input" />
        </>
      ) : (
        <>
          <label className="submit-label">{t("fCategory")} *</label>
          <select className="submit-input" value={form.category}
            onChange={(e) => set("category", e.target.value)} data-testid="submit-category-select">
            <option value="">{t("fCategorySelect")}</option>
            {placeCategories.map((c) => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </>
      )}

      <label className="submit-label">{t("fDescription")}</label>
      <textarea className="submit-input" rows={3} value={form.description}
        onChange={(e) => set("description", e.target.value)}
        placeholder={t("fDescPh")} data-testid="submit-description-input" />

      <label className="submit-label">{t("fLocation")}</label>
      <input className="submit-input" type="url" value={form.location_url}
        onChange={(e) => set("location_url", e.target.value)}
        placeholder="https://maps.app.goo.gl/…" data-testid="submit-location-input" />

      <label className="submit-label">{t("fContact")}</label>
      <input className="submit-input" type="tel" value={form.phone}
        onChange={(e) => set("phone", e.target.value)}
        placeholder="+506 8888 1111" data-testid="submit-phone-input" />

      <label className="submit-label">{t("fWebsite")}</label>
      <input className="submit-input" type="url" value={form.website_url}
        onChange={(e) => set("website_url", e.target.value)}
        placeholder="https://…" data-testid="submit-website-input" />

      <label className="submit-label">{t("fBusinessEmail")}</label>
      <input className="submit-input" type="email" value={form.email}
        onChange={(e) => set("email", e.target.value)}
        placeholder="hello@…" data-testid="submit-business-email-input" />

      <label className="submit-label">{t("fImage")}</label>
      {form.featured_image ? (
        <div className="submit-image-preview">
          <img src={imgSrc(form.featured_image)} alt="preview" />
          <button className="btn-ghost btn-small" onClick={() => set("featured_image", "")} data-testid="submit-image-remove">{t("removeImg")}</button>
        </div>
      ) : null}
      <input ref={fileRef} type="file" accept="image/*" onChange={onImage} style={{ display: "none" }} data-testid="submit-image-input" />
      <button className="btn-ghost btn-small" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="submit-image-upload">
        {uploading ? t("uploading") : form.featured_image ? t("replaceImage") : t("uploadImage")}
      </button>

      <label className="submit-label">{t("fEmail")} *</label>
      <input className="submit-input" type="email" value={form.submitter_email}
        onChange={(e) => set("submitter_email", e.target.value)}
        placeholder="you@example.com" data-testid="submit-email-input" />

      {/* Honeypot — hidden from humans, catches bots */}
      <input tabIndex={-1} autoComplete="off" className="submit-honeypot" value={form.website}
        onChange={(e) => set("website", e.target.value)} aria-hidden="true" />

      {error ? <p className="submit-error" data-testid="submit-error">{error}</p> : null}

      <div className="submit-actions">
        <button className="btn-ghost" onClick={onClose} data-testid="submit-cancel-btn">{t("cancel")}</button>
        <button className="btn-primary" onClick={submit} disabled={submitting} data-testid="submit-send-btn">
          {submitting ? t("submitting") : t("submitReview")}
        </button>
      </div>
    </div>
  );
}

export function SubmitBar({ compact = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState(null); // null = chooser step, "event" | "place"
  const close = () => { setOpen(false); setKind(null); };
  const trigger = (
    <button className="submit-cta" onClick={() => setOpen(true)} data-testid="add-new-btn">
      <Plus size={16} strokeWidth={2} /> {t("addNew")}
    </button>
  );
  return (
    <>
      {compact ? trigger : <div className="submit-bar" data-testid="submit-bar">{trigger}</div>}
      {open ? (
        <div className="submit-overlay" onClick={close} data-testid="submit-overlay">
          {kind ? (
            <SubmitForm kind={kind} onClose={close} onBack={() => setKind(null)} />
          ) : (
            <div className="submit-modal" onClick={(e) => e.stopPropagation()} data-testid="add-new-chooser">
              <button className="submit-close" onClick={close} aria-label="Close" data-testid="submit-close"><X size={18} /></button>
              <h2 className="submit-title">{t("addNewTitle")}</h2>
              <p className="submit-sub">{t("submitSub")}</p>
              <div className="submit-choices">
                <button className="submit-choice" onClick={() => setKind("event")} data-testid="add-new-choice-event">
                  <CalendarPlus size={22} strokeWidth={2} /> {t("addEvent")}
                </button>
                <button className="submit-choice" onClick={() => setKind("place")} data-testid="add-new-choice-place">
                  <MapPinPlus size={22} strokeWidth={2} /> {t("addPlace")}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
